<template>
  <div class="home-page">
    <section class="hero-section" aria-labelledby="home-hero-title">
      <img
        class="hero-bg"
        src="/img/hero-project-inspection.webp"
        alt="住宅项目精装入场查验与工程质量过程管控现场"
      />
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="hero-main">
          <nav class="breadcrumb" aria-label="面包屑">
            <span>首页</span>
          </nav>
          <p class="eyebrow">江苏道鼎升工程检测有限公司</p>
          <h1 id="home-hero-title">住宅项目精装入场查验与工程质量过程管控服务</h1>
          <p class="hero-subtitle">
            江苏道鼎升工程检测有限公司面向开发商、建设单位、物业单位和住宅交付项目，提供精装入场阶段查验、过程质量巡检、分户查验、一房一验、交付前预验收、整改复查和工程质量检测服务，协助项目在交付前发现问题、形成清单并推动整改闭环。
          </p>
          <div class="hero-actions">
            <router-link class="btn btn-primary" to="/services">了解对公服务</router-link>
            <a class="btn btn-secondary" href="#lead-form">咨询项目查验</a>
          </div>
          <div class="hero-trust">
            <span v-for="item in heroTrustPoints" :key="item">
              <i class="el-icon-check"></i>
              {{ item }}
            </span>
          </div>
        </div>
        <aside class="hero-panel" aria-label="项目查验重点">
          <strong>项目质量管控重点</strong>
          <ul>
            <li v-for="item in heroChecklist" :key="item">{{ item }}</li>
          </ul>
        </aside>
      </div>
    </section>

    <section class="section summary-section" aria-labelledby="summary-title">
      <div class="section-inner summary-layout">
        <div class="summary-copy">
          <p class="section-kicker">Company Positioning</p>
          <h2 id="summary-title">以江苏道鼎升工程检测有限公司为服务主体</h2>
          <p>{{ companySummary }}</p>
        </div>
        <div class="summary-card">
          <strong>核心信息</strong>
          <dl>
            <div>
              <dt>公司主体</dt>
              <dd>{{ company.name }}</dd>
            </div>
            <div>
              <dt>服务重点</dt>
              <dd>对公工程查验、精装入场查验、工程质量过程管控</dd>
            </div>
            <div>
              <dt>主要客户</dt>
              <dd>开发商、建设单位、物业单位、工程项目、住宅交付项目</dd>
            </div>
            <div>
              <dt>服务区域</dt>
              <dd>{{ company.serviceArea }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <section class="section service-types-section" aria-labelledby="service-type-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Service Objects</p>
          <h2 id="service-type-title">先区分服务对象，再匹配查验方案</h2>
          <p>首页以对公工程查验为主，业主个人验房作为独立入口展示，避免项目客户和个人业主的需求混在同一套表达里。</p>
        </div>
        <div class="service-type-grid">
          <article
            v-for="item in serviceTypeCards"
            :key="item.title"
            :class="['service-type-card', item.type]"
          >
            <div class="service-type-head">
              <span>{{ item.type === "primary" ? "主业务" : "独立入口" }}</span>
              <h3>{{ item.title }}</h3>
            </div>
            <p>{{ item.description }}</p>
            <div class="mini-columns">
              <div>
                <strong>面向</strong>
                <ul>
                  <li v-for="audience in item.audience" :key="audience">{{ audience }}</li>
                </ul>
              </div>
              <div>
                <strong>服务内容</strong>
                <ul>
                  <li v-for="service in item.services" :key="service">{{ service }}</li>
                </ul>
              </div>
            </div>
            <router-link class="text-link" :to="item.path">进入页面</router-link>
          </article>
        </div>
      </div>
    </section>

    <section class="section pain-section" aria-labelledby="pain-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Quality Risk Control</p>
          <h2 id="pain-title">把交付风险前置到精装入场和施工过程</h2>
          <p>对公服务不只在房屋交付时到场，而是在项目质量管理过程中协助发现、记录、汇总和复查问题。</p>
        </div>
        <div class="pain-grid">
          <div class="pain-item" v-for="item in painPoints" :key="item.title">
            <i :class="item.icon"></i>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="services" class="section services-section" aria-labelledby="services-title">
      <div class="section-inner">
        <div class="section-heading wide-heading">
          <p class="section-kicker">Core Public Services</p>
          <h2 id="services-title">对公工程查验服务入口</h2>
          <p>服务排序以项目质量管理链路为准，从精装入场、过程巡检到分户查验、交付前预验收、整改复查和工程质量检测，业主验房作为独立入口放在末位。</p>
        </div>
        <div class="services-grid">
          <article class="service-card" v-for="service in services" :key="service.slug">
            <img :src="service.image" :alt="service.shortTitle + '服务场景示意图'" />
            <div class="service-card-body">
              <span>{{ service.serviceType }}</span>
              <h3>{{ service.shortTitle }}</h3>
              <p class="service-target"><strong>适用对象：</strong>{{ service.targetUsers.join("、") }}</p>
              <p>{{ service.summary }}</p>
              <router-link class="text-link" :to="service.path">了解服务</router-link>
            </div>
          </article>
          <article class="service-card owner-entry">
            <img :src="ownerService.image" alt="业主新房验房与收房陪验服务场景" />
            <div class="service-card-body">
              <span>对私独立入口</span>
              <h3>{{ ownerService.shortTitle }}</h3>
              <p class="service-target"><strong>适用对象：</strong>新房业主、精装修房业主、毛坯房业主</p>
              <p>{{ ownerService.summary }}</p>
              <router-link class="text-link" :to="ownerService.path">查看业主验房</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section id="process" class="section process-section" aria-labelledby="process-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Delivery Workflow</p>
          <h2 id="process-title">对公项目服务流程</h2>
          <p>围绕项目批次、现场条件和交付节点，把查验、记录、报告、整改和复查组织成可执行的闭环。</p>
        </div>
        <ol class="process-list">
          <li v-for="(step, index) in process" :key="step.title">
            <span>{{ index + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="section inspection-section" aria-labelledby="inspection-title">
      <div class="section-inner inspection-layout">
        <div>
          <p class="section-kicker">Inspection Items</p>
          <h2 id="inspection-title">常见查验与检测内容</h2>
          <p>
            查验内容会根据项目阶段、交付标准、户型批次和现场目标调整，重点让问题可定位、可分类、可整改、可复查。
          </p>
          <ul class="inspection-grid">
            <li v-for="item in commonInspectionItems" :key="item">
              <i class="el-icon-search"></i>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
        <img
          class="inspection-image"
          src="/img/household-inspection.webp"
          alt="住宅分户查验与一房一验现场示意图"
        />
      </div>
    </section>

    <section class="section proof-section" aria-labelledby="proof-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Report Samples</p>
          <h2 id="proof-title">报告样张与问题清单示意</h2>
          <p>以下内容为样张展示，用于说明资料交付形式；不包含真实客户名称、项目名称、证书编号或虚构评价。</p>
        </div>
        <div class="proof-grid">
          <article v-for="item in proofAssets" :key="item.title" class="proof-card">
            <img :src="item.image" :alt="item.title + '示意图'" />
            <div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section area-section" aria-labelledby="area-title">
      <div class="section-inner area-layout">
        <div>
          <p class="section-kicker">Service Area</p>
          <h2 id="area-title">服务覆盖江苏、上海及长三角区域</h2>
          <p>
            我们为江苏、上海及长三角区域的开发商、建设单位、物业单位和住宅交付项目提供精装入场查验、过程质量巡检、分户查验、交付前预验收、工程质量检测及整改复查服务。
          </p>
          <div class="area-tags">
            <span v-for="city in company.serviceCities" :key="city">{{ city }}</span>
          </div>
        </div>
        <div class="area-visual">
          <img src="/img/yangtze-delta-building.webp" alt="江苏上海长三角住宅项目服务区域建筑示意图" />
          <div class="area-cards">
            <article v-for="item in serviceAreas" :key="item.title">
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section id="cases" class="section cases-section" aria-labelledby="cases-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">Project References</p>
          <h2 id="cases-title">对公项目服务记录</h2>
          <p>以下案例来自现有站点资料，页面不新增虚构客户评价、评分或服务数量；真实项目照片和报告样张建议后续由人工核对后补充。</p>
        </div>
        <div class="cases-grid">
          <article class="case-card" v-for="item in cases" :key="item.title">
            <img :src="item.image" :alt="item.title + '服务场景示意图'" />
            <div class="case-content">
              <h3>{{ item.title }}</h3>
              <p><strong>项目类型：</strong>{{ item.projectType }}</p>
              <p><strong>服务内容：</strong>{{ item.service }}</p>
              <p><strong>交付成果：</strong>{{ item.result }}</p>
              <p><strong>项目价值：</strong>{{ item.value }}</p>
              <router-link class="case-link" to="/cases">查看更多案例</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section trust-section" aria-labelledby="trust-title">
      <div class="section-inner trust-layout">
        <div class="trust-copy">
          <p class="section-kicker">Trust</p>
          <h2 id="trust-title">为什么选择江苏道鼎升</h2>
          <p>
            我们以第三方工程查验视角参与项目质量管理，重视标准化流程、现场记录、报告输出和整改闭环，不夸大承诺，只提供可核查的专业服务。
          </p>
        </div>
        <div class="trust-grid">
          <div class="trust-item" v-for="item in whyChooseUs" :key="item.title">
            <i class="el-icon-medal"></i>
            <div>
              <strong>{{ item.title }}</strong>
              <span>{{ item.description }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section faq-section" aria-labelledby="faq-title">
      <div class="section-inner">
        <div class="section-heading">
          <p class="section-kicker">FAQ</p>
          <h2 id="faq-title">对公工程查验常见问题</h2>
          <p>围绕精装入场查验、过程质量巡检、分户查验、报告交付和整改复查的高频问题说明。</p>
        </div>
        <div class="faq-list">
          <details v-for="item in faqs" :key="item.question">
            <summary>{{ item.question }}</summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <section id="lead-form" class="section lead-section" aria-labelledby="lead-title">
      <div class="section-inner lead-layout">
        <div class="lead-copy">
          <p class="section-kicker">Contact</p>
          <h2 id="lead-title">咨询对公项目查验方案</h2>
          <p>
            请填写项目基础信息，我们会结合项目城市、交付批次、预计户数、精装入场或交付节点，沟通适合的查验范围、人员安排和报告口径。
          </p>
          <div class="contact-panel">
            <p><strong>电话：</strong>{{ company.phone }}</p>
            <p><strong>邮箱：</strong>{{ company.email }}</p>
            <p><strong>地址：</strong>{{ company.address }}</p>
          </div>
          <router-link class="owner-link" to="/owner-inspection">个人业主验房请进入独立页面</router-link>
        </div>

        <el-form
          class="lead-form"
          ref="leadForm"
          :model="leadForm"
          :rules="rules"
          label-width="132px"
        >
          <el-form-item label="公司名称" prop="companyName">
            <el-input v-model="leadForm.companyName" placeholder="请输入公司名称"></el-input>
          </el-form-item>
          <el-form-item label="项目名称" prop="projectName">
            <el-input v-model="leadForm.projectName" placeholder="请输入项目名称"></el-input>
          </el-form-item>
          <el-form-item label="项目城市" prop="city">
            <el-input v-model="leadForm.city" placeholder="如：苏州、南京、上海"></el-input>
          </el-form-item>
          <el-form-item label="项目类型" prop="projectType">
            <el-select v-model="leadForm.projectType" placeholder="请选择项目类型">
              <el-option v-for="type in projectTypes" :key="type" :label="type" :value="type"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="预计查验户数" prop="households">
            <el-input v-model="leadForm.households" placeholder="如：320 户"></el-input>
          </el-form-item>
          <el-form-item label="预计节点" prop="deliveryTime">
            <el-input v-model="leadForm.deliveryTime" placeholder="如：精装入场、开放日、2026年10月交付"></el-input>
          </el-form-item>
          <el-form-item label="需要的服务" prop="serviceNeed">
            <el-checkbox-group v-model="leadForm.serviceNeed">
              <el-checkbox v-for="item in serviceNeeds" :key="item" :label="item"></el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="联系人" prop="contactName">
            <el-input v-model="leadForm.contactName" placeholder="请输入联系人"></el-input>
          </el-form-item>
          <el-form-item label="手机号" prop="phone">
            <el-input v-model="leadForm.phone" placeholder="请输入手机号"></el-input>
          </el-form-item>
          <el-form-item label="微信号" prop="wechat">
            <el-input v-model="leadForm.wechat" placeholder="请输入微信号"></el-input>
          </el-form-item>
          <el-form-item label="备注" prop="remark">
            <el-input
              v-model="leadForm.remark"
              type="textarea"
              :rows="4"
              placeholder="可补充楼栋范围、交付批次、现场阶段、报告要求等"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" class="form-submit" @click="submitLead">提交需求</el-button>
            <el-button @click="resetLead">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">{{ company.icp }}</a>
      <a :href="company.publicSecurityUrl" target="_blank" rel="noopener">{{ company.publicSecurityRecord }}</a>
    </div>
  </div>
</template>

<script>
import {
  commonInspectionItems,
  companyInfo,
  companySummary,
  homeFaqs,
  ownerInspectionService,
  proofAssets,
  projectCases,
  publicServices,
  serviceAreas,
  serviceProcessDetails,
  serviceTypeCards,
  whyChooseUs
} from "../data/services";
import {
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData,
  websiteSchema
} from "../utils/seo";

export default {
  name: "Home",
  data() {
    return {
      company: companyInfo,
      companySummary: companySummary,
      services: publicServices,
      ownerService: ownerInspectionService,
      serviceTypeCards: serviceTypeCards,
      process: serviceProcessDetails,
      cases: projectCases,
      whyChooseUs: whyChooseUs,
      faqs: homeFaqs,
      serviceAreas: serviceAreas,
      commonInspectionItems: commonInspectionItems,
      proofAssets: proofAssets,
      heroTrustPoints: ["精装入场同步查验", "过程质量巡检", "分户问题清单", "整改复查闭环", "覆盖江苏、上海及长三角区域"],
      heroChecklist: ["精装入场前发现前置问题", "施工过程持续巡检记录", "分户问题清单便于整改", "交付前预验收降低集中风险"],
      painPoints: [
        {
          icon: "el-icon-office-building",
          title: "精装入场问题未前置",
          description: "基层、门窗、防水、管线和移交条件问题如果到交付前才暴露，会放大返工成本。"
        },
        {
          icon: "el-icon-s-operation",
          title: "过程质量缺少跟踪",
          description: "施工过程中的共性问题需要持续巡检、阶段汇总和整改状态跟踪。"
        },
        {
          icon: "el-icon-document-checked",
          title: "批量分户记录不统一",
          description: "按户、楼栋和专业类别输出问题清单，便于工程、客服、物业和施工单位协同。"
        },
        {
          icon: "el-icon-warning-outline",
          title: "交付前投诉风险集中",
          description: "交付前预验收能把高频问题提前记录和整改，降低集中交付阶段沟通压力。"
        },
        {
          icon: "el-icon-refresh",
          title: "整改缺少复查闭环",
          description: "整改完成后需要逐项复核，记录已整改、未整改、整改不到位和新增问题。"
        },
        {
          icon: "el-icon-data-analysis",
          title: "项目质量资料不够清晰",
          description: "报告、清单、照片和阶段汇总让质量问题更容易追溯、复盘和推进。"
        }
      ],
      projectTypes: ["精装住宅", "毛坯住宅", "商业", "公寓", "别墅", "公共区域", "其他"],
      serviceNeeds: ["精装入场查验", "过程质量巡检", "分户查验 / 一房一验", "交付前预验收", "整改复查", "工程质量检测", "报告输出", "其他"],
      leadForm: {
        companyName: "",
        projectName: "",
        city: "",
        projectType: "",
        households: "",
        deliveryTime: "",
        serviceNeed: [],
        contactName: "",
        phone: "",
        wechat: "",
        remark: ""
      },
      rules: {
        companyName: [{ required: true, message: "请输入公司名称", trigger: "blur" }],
        projectName: [{ required: true, message: "请输入项目名称", trigger: "blur" }],
        city: [{ required: true, message: "请输入项目城市", trigger: "blur" }],
        projectType: [{ required: true, message: "请选择项目类型", trigger: "change" }],
        serviceNeed: [{ type: "array", required: true, message: "请选择需要的服务", trigger: "change" }],
        contactName: [{ required: true, message: "请输入联系人", trigger: "blur" }],
        phone: [
          { required: true, message: "请输入手机号", trigger: "blur" },
          { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号码", trigger: "blur" }
        ]
      }
    };
  },
  mounted() {
    setSeo({
      title: "精装入场查验与工程质量过程管控｜江苏道鼎升工程检测",
      description:
        "江苏道鼎升工程检测有限公司面向开发商、建设单位、物业单位和住宅交付项目，提供精装入场查验、过程质量巡检、分户查验、一房一验、交付前预验收、整改复查和工程质量检测服务。",
      path: "/",
      image: "/img/hero-project-inspection.webp"
    });
    setStructuredData([websiteSchema(), organizationSchema(), localBusinessSchema()]);
  },
  methods: {
    submitLead() {
      this.$refs.leadForm.validate(valid => {
        if (!valid) {
          return;
        }
        this.$message({
          message: "已收到您的项目查验需求，我们会尽快与您联系。",
          type: "success"
        });
        this.resetLead();
      });
    },
    resetLead() {
      this.$refs.leadForm.resetFields();
    }
  }
};
</script>

<style lang="scss" scoped>
.home-page {
  background: #f4f6f8;
  color: #1f2d3d;
}

.hero-section {
  position: relative;
  min-height: 680px;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(7, 25, 40, 0.92), rgba(11, 45, 68, 0.76), rgba(12, 48, 66, 0.34)),
    linear-gradient(180deg, rgba(7, 25, 40, 0.18), rgba(7, 25, 40, 0.76));
}

.hero-content {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 44px;
  align-items: end;
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
  color: #fff;
}

.breadcrumb {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.78);
  margin-bottom: 24px;
}

.eyebrow,
.section-kicker {
  color: #c8872a;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 12px;
}

.hero-content h1 {
  max-width: 940px;
  font-size: 48px;
  line-height: 1.22;
  font-weight: 700;
  margin: 0;
}

.hero-subtitle {
  max-width: 980px;
  margin: 24px 0 0;
  font-size: 18px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.9);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 34px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
}

.btn-primary {
  color: #fff;
  background: #0c4a6e;
}

.btn-secondary {
  color: #fff;
  background: #c8872a;
}

.hero-trust {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 36px;
}

.hero-trust span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(255, 255, 255, 0.26);
  background: rgba(255, 255, 255, 0.11);
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 14px;
  backdrop-filter: blur(8px);
}

.hero-trust i {
  color: #e7b052;
}

.hero-panel {
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(8, 32, 48, 0.72);
  backdrop-filter: blur(14px);
  border-radius: 8px;
  padding: 26px;
}

.hero-panel strong {
  display: block;
  font-size: 20px;
  margin-bottom: 18px;
}

.hero-panel ul {
  list-style: none;
  display: grid;
  gap: 14px;
}

.hero-panel li {
  position: relative;
  padding-left: 18px;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.7;
}

.hero-panel li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 11px;
  width: 6px;
  height: 6px;
  background: #c8872a;
}

.section {
  padding: 82px 0;
}

.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.section-heading {
  max-width: 760px;
  margin-bottom: 34px;
}

.wide-heading {
  max-width: 900px;
}

.section-heading h2,
.summary-copy h2,
.area-layout h2,
.trust-copy h2,
.lead-copy h2,
.inspection-layout h2 {
  font-size: 34px;
  line-height: 1.35;
  color: #102d42;
  margin: 0 0 14px;
}

.section-heading p,
.summary-copy p,
.area-layout p,
.trust-copy p,
.lead-copy p,
.inspection-layout p {
  color: #5e6b76;
  font-size: 16px;
  line-height: 1.85;
}

.summary-section,
.services-section,
.faq-section {
  background: #fff;
}

.summary-layout,
.area-layout,
.inspection-layout,
.trust-layout,
.lead-layout {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 42px;
  align-items: start;
}

.summary-card,
.service-type-card,
.pain-item,
.service-card,
.case-card,
.proof-card,
.trust-item,
.contact-panel,
.lead-form {
  border: 1px solid #dfe6eb;
  border-radius: 8px;
  background: #fff;
}

.summary-card {
  padding: 30px;
  box-shadow: 0 18px 40px rgba(15, 45, 66, 0.06);
}

.summary-card strong {
  display: block;
  color: #102d42;
  font-size: 20px;
  margin-bottom: 18px;
}

.summary-card dl {
  margin: 0;
}

.summary-card dl > div {
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 12px;
  padding: 14px 0;
  border-top: 1px solid #e9eef2;
}

.summary-card dt {
  color: #0c4a6e;
  font-weight: 700;
}

.summary-card dd {
  margin: 0;
  color: #4d5b66;
  line-height: 1.7;
}

.service-type-grid {
  display: grid;
  grid-template-columns: 1.18fr 0.82fr;
  gap: 20px;
}

.service-type-card {
  padding: 30px;
  box-shadow: 0 18px 40px rgba(15, 45, 66, 0.06);
}

.service-type-card.primary {
  border-color: rgba(12, 74, 110, 0.32);
}

.service-type-card.secondary {
  background: #f8fafc;
}

.service-type-head span,
.service-card-body > span {
  display: inline-flex;
  margin-bottom: 12px;
  padding: 6px 10px;
  border-radius: 4px;
  background: #eef5f8;
  color: #0c4a6e;
  font-size: 13px;
  font-weight: 700;
}

.service-type-head h3,
.service-card h3,
.case-card h3,
.proof-card h3,
.process-list h3 {
  color: #102d42;
  font-size: 21px;
  line-height: 1.45;
  margin: 0 0 12px;
}

.service-type-card p,
.service-card p,
.case-content p,
.proof-card p,
.pain-item p {
  color: #5e6b76;
  line-height: 1.75;
}

.mini-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin: 22px 0;
}

.mini-columns strong {
  display: block;
  color: #102d42;
  margin-bottom: 10px;
}

.mini-columns ul {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mini-columns li {
  padding: 7px 9px;
  border-radius: 4px;
  background: #edf2f6;
  color: #334451;
  font-size: 13px;
}

.text-link,
.case-link,
.owner-link {
  color: #0c4a6e;
  font-weight: 700;
  text-decoration: none;
}

.pain-section,
.proof-section {
  background: #f4f6f8;
}

.pain-grid,
.services-grid,
.cases-grid,
.proof-grid,
.trust-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.pain-item {
  min-height: 178px;
  padding: 26px;
}

.pain-item i {
  color: #c8872a;
  font-size: 28px;
}

.pain-item h3 {
  font-size: 19px;
  color: #102d42;
  margin: 16px 0 0;
}

.services-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.service-card {
  overflow: hidden;
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  min-height: 250px;
  box-shadow: 0 18px 42px rgba(15, 45, 66, 0.06);
}

.service-card img {
  width: 100%;
  height: 100%;
  min-height: 250px;
  object-fit: cover;
  display: block;
}

.service-card-body {
  padding: 26px;
}

.service-target {
  color: #0c4a6e !important;
  font-size: 14px;
  margin-bottom: 12px;
}

.owner-entry {
  background: #f8fafc;
}

.process-section {
  background: #102d42;
  color: #fff;
}

.process-section .section-heading h2,
.process-section .section-heading p {
  color: #fff;
}

.process-section .section-heading p {
  color: rgba(255, 255, 255, 0.78);
}

.process-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.process-list li {
  position: relative;
  min-height: 180px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.process-list span {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  background: #c8872a;
  font-weight: 700;
  margin-bottom: 18px;
}

.process-list h3 {
  color: #fff;
  margin: 0;
  font-size: 18px;
}

.process-list p {
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
  line-height: 1.7;
  margin: 10px 0 0;
}

.inspection-section {
  background: #fff;
}

.inspection-layout {
  align-items: center;
}

.inspection-grid {
  list-style: none;
  padding: 0;
  margin: 26px 0 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.inspection-grid li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  min-height: 84px;
  padding: 16px;
  border: 1px solid #dfe6eb;
  border-radius: 8px;
  background: #f8fafc;
  color: #3d4b56;
  line-height: 1.7;
}

.inspection-grid i {
  color: #0c4a6e;
  font-size: 20px;
  margin-top: 3px;
}

.inspection-image {
  width: 100%;
  min-height: 520px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
  box-shadow: 0 24px 52px rgba(15, 45, 66, 0.12);
}

.proof-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.proof-card {
  overflow: hidden;
}

.proof-card img {
  width: 100%;
  height: 210px;
  object-fit: cover;
  display: block;
}

.proof-card div {
  padding: 24px;
}

.area-section {
  background: #eef3f7;
}

.area-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
}

.area-tags span {
  border: 1px solid #cfdae2;
  background: #fff;
  border-radius: 4px;
  color: #0c4a6e;
  font-weight: 700;
  padding: 9px 12px;
}

.area-visual img {
  width: 100%;
  height: 240px;
  object-fit: cover;
  display: block;
  border-radius: 8px;
  margin-bottom: 14px;
}

.area-cards {
  display: grid;
  gap: 14px;
}

.area-cards article {
  border: 1px solid #dfe6eb;
  border-radius: 8px;
  background: #fff;
  padding: 22px;
}

.area-cards h3 {
  color: #102d42;
  font-size: 20px;
  margin: 0 0 10px;
}

.case-card {
  overflow: hidden;
}

.case-card img {
  width: 100%;
  height: 220px;
  display: block;
  object-fit: cover;
}

.case-content {
  padding: 24px;
}

.case-content p {
  font-size: 15px;
  margin: 10px 0 0;
}

.case-link {
  display: inline-flex;
  margin-top: 18px;
}

.trust-section {
  background: #102d42;
  color: #fff;
}

.trust-copy h2,
.trust-copy p {
  color: #fff;
}

.trust-copy p {
  color: rgba(255, 255, 255, 0.82);
}

.trust-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.trust-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.16);
  color: #fff;
}

.trust-item i {
  color: #e7b052;
  font-size: 22px;
  margin-top: 3px;
}

.trust-item strong {
  display: block;
  margin-bottom: 7px;
}

.trust-item span {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  font-size: 14px;
}

.faq-list {
  display: grid;
  gap: 12px;
}

.faq-list details {
  border: 1px solid #dfe6eb;
  border-radius: 8px;
  background: #fff;
  padding: 0 22px;
}

.faq-list summary {
  cursor: pointer;
  list-style: none;
  padding: 20px 0;
  color: #102d42;
  font-size: 17px;
  font-weight: 700;
}

.faq-list summary::-webkit-details-marker {
  display: none;
}

.faq-list p {
  border-top: 1px solid #edf2f1;
  color: #5e6b76;
  line-height: 1.8;
  padding: 16px 0 20px;
  margin: 0;
}

.lead-section {
  background: #eef3f7;
}

.lead-layout {
  grid-template-columns: 0.82fr 1.18fr;
}

.contact-panel {
  margin-top: 24px;
  padding: 22px;
}

.contact-panel p {
  margin: 0 0 12px;
  color: #102d42;
}

.contact-panel p:last-child {
  margin-bottom: 0;
}

.owner-link {
  display: inline-flex;
  margin-top: 18px;
}

.lead-form {
  padding: 30px;
  box-shadow: 0 18px 42px rgba(15, 45, 66, 0.08);
}

.lead-form .el-select {
  width: 100%;
}

.form-submit {
  background: #0c4a6e;
  border-color: #0c4a6e;
}

@media (max-width: 1080px) {
  .hero-content,
  .summary-layout,
  .service-type-grid,
  .pain-grid,
  .services-grid,
  .cases-grid,
  .inspection-layout,
  .process-list,
  .area-layout,
  .proof-grid,
  .trust-layout,
  .lead-layout {
    grid-template-columns: 1fr;
  }

  .hero-section {
    min-height: auto;
    padding: 88px 0;
  }

  .hero-panel {
    max-width: 520px;
  }

  .service-card {
    grid-template-columns: 240px minmax(0, 1fr);
  }
}

@media (max-width: 640px) {
  .hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .hero-section {
    padding-top: 36px;
    padding-bottom: 44px;
  }

  .hero-content {
    gap: 0;
  }

  .breadcrumb {
    display: none;
  }

  .hero-content h1 {
    font-size: 28px;
    line-height: 1.26;
  }

  .hero-subtitle {
    margin-top: 14px;
    font-size: 15px;
    line-height: 1.62;
  }

  .hero-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 16px;
  }

  .btn {
    width: 100%;
    min-height: 42px;
    padding: 0 10px;
    font-size: 14px;
  }

  .hero-trust {
    margin-top: 14px;
    gap: 8px;
  }

  .hero-trust span {
    padding: 8px 10px;
    font-size: 13px;
  }

  .hero-trust span:nth-child(n+4),
  .hero-panel {
    display: none;
  }

  .section {
    padding: 56px 0;
  }

  .section-heading h2,
  .summary-copy h2,
  .area-layout h2,
  .trust-copy h2,
  .lead-copy h2,
  .inspection-layout h2 {
    font-size: 26px;
  }

  .summary-card dl > div,
  .mini-columns,
  .inspection-grid,
  .trust-grid {
    grid-template-columns: 1fr;
  }

  .service-card {
    grid-template-columns: 1fr;
  }

  .service-card img {
    height: 220px;
    min-height: 0;
  }

  .inspection-image {
    min-height: 280px;
  }

  .lead-form {
    padding: 22px 16px;
  }

  .lead-form ::v-deep .el-form-item__label {
    float: none;
    display: block;
    text-align: left;
    width: auto !important;
    padding: 0 0 8px;
  }

  .lead-form ::v-deep .el-form-item__content {
    margin-left: 0 !important;
  }
}
</style>
